import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { 
  InformationCircleIcon, 
  PhotoIcon, 
  ScaleIcon, 
  HeartIcon, 
  UserGroupIcon, 
  DocumentTextIcon,
  CalendarIcon,
  HomeIcon,
  TagIcon,
  UserIcon,
  GlobeAltIcon,
  BeakerIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Bird } from '../api/birds';
import { BirdStatus, Gender } from '../utils/enums';
import BirdPhotos from './PhotoGallery/BirdPhotos';
import { getBirdDisplayInfo } from '../utils/birdDisplay';
import { getGenderColorClass, getGenderIcon, GENDER_ICON_FEMALE, GENDER_ICON_MALE } from '../utils/genderDisplay';
import { getPhotoUrl } from '../api/photos';
import { updateBirdAvatar } from '../api/birds';
import api from '../api/client';
import BirdGeneticsPanel from './genetics/BirdGeneticsPanel';
import toast from 'react-hot-toast';

interface BirdDetailsProps {
  bird: Bird;
  onUpdate: (bird: Bird) => void;
  onClose?: () => void;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function BirdDetails({ bird, onUpdate, onClose }: BirdDetailsProps) {
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    setSelectedTab(0);
  }, [bird.id]);

  const handleAvatarChange = async (photoId: string) => {
    try {
      const updatedBird = await updateBirdAvatar(bird.id, photoId);
      onUpdate(updatedBird);
    } catch (error) {
      console.error('Failed to update avatar:', error);
      toast.error('Failed to set avatar photo');
    }
  };

  const categories = [
    { 
      name: 'Overview', 
      icon: InformationCircleIcon,
      content: <BirdOverview bird={bird} /> 
    },
    {
      name: 'Photos',
      icon: PhotoIcon,
      content: (
        <BirdPhotos
          birdId={bird.id}
          initialPhotos={bird.photos || []}
          onAvatarChange={handleAvatarChange}
          selectedAvatar={bird.avatarPhotoId}
          onPhotosChange={(photos) => onUpdate({ ...bird, photos })}
        />
      )
    },
    { 
      name: 'Physical Info', 
      icon: ScaleIcon,
      content: <BirdPhysicalInfo bird={bird} /> 
    },
    { 
      name: 'Health Records', 
      icon: HeartIcon,
      content: <BirdHealthRecords bird={bird} /> 
    },
    { 
      name: 'Family Tree', 
      icon: UserGroupIcon,
      content: <BirdFamilyTree bird={bird} /> 
    },
    {
      name: 'Genetics',
      icon: BeakerIcon,
      content: <BirdGeneticsPanel bird={bird} onImported={() => onUpdate(bird)} />,
    },
    { 
      name: 'Notes & Documents', 
      icon: DocumentTextIcon,
      content: <BirdNotesAndDocuments bird={bird} /> 
    },
  ];

  return (
    <div className="w-full">
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6 overflow-x-auto">
          {categories.map((category) => (
            <Tab
              key={category.name}
              className={({ selected }) =>
                classNames(
                  'flex-shrink-0 rounded-lg py-2.5 px-3 text-sm font-medium leading-5 flex items-center justify-center space-x-2',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white shadow text-cyan-700 border border-indigo-200'
                    : 'text-slate-600 hover:bg-white/[0.8] hover:text-gray-800'
                )
              }
            >
              <category.icon className="h-4 w-4" />
              <span className="hidden sm:block whitespace-nowrap">{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-2">
          {categories.map((category, idx) => (
            <Tab.Panel
              key={idx}
              className={classNames(
                'rounded-xl bg-white p-6 shadow-sm border',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2'
              )}
            >
              {category.content}
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
        {onClose && (
          <button
            type="button"
            className={
              selectedTab < categories.length - 1
                ? 'rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80'
                : 'btn btn-primary text-sm'
            }
            onClick={onClose}
          >
            Close
          </button>
        )}
        {selectedTab < categories.length - 1 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm ring-1 ring-inset ring-cyan-200 hover:bg-cyan-50"
            onClick={() => setSelectedTab((index) => Math.min(index + 1, categories.length - 1))}
          >
            Next
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function BirdOverview({ bird }: { bird: Bird }) {
  const getStatusColor = (status: BirdStatus) => {
    switch (status) {
      case BirdStatus.BREEDING:
        return 'bg-emerald-100 text-emerald-800 border-green-200';
      case BirdStatus.SOLD:
        return 'bg-violet-100 text-violet-800 border-gray-200';
      case BirdStatus.DECEASED:
        return 'bg-rose-100 text-rose-800 border-red-200';
      default:
        return 'bg-cyan-100 text-cyan-800 border-blue-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="border-b border-gray-200 pb-6">
        <div className="flex items-start space-x-4">
          {bird.avatarPhotoId && (
            <div className="flex-shrink-0">
              <img
                src={getPhotoUrl(bird.avatarPhotoId)}
                alt={bird.bandId}
                className="h-24 w-24 rounded-lg object-cover"
              />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-slate-900">{bird.bandId}</h2>
              <span className={`text-2xl font-bold ${getGenderColorClass(bird.gender)}`}>
                {getGenderIcon(bird.gender)}
              </span>
            </div>
            <p className="text-lg text-slate-600 italic mt-1">
              {bird.species?.commonName || 'Unknown Species'}
            </p>
            {bird.species && (
              <p className="text-md text-slate-500 mt-1">
                {bird.species.genus} {bird.species.species}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(bird.status)}`}>
                {bird.status}
              </span>
              {bird.status === BirdStatus.FOR_SALE && bird.sellPrice && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800 border border-green-200">
                  R{bird.sellPrice}
                </span>
              )}
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                Cage {bird.cageNumber}
              </span>
              {bird.mutationName && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-cyan-100 text-cyan-800">
                  {bird.mutationName}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <TagIcon className="h-5 w-5 mr-2 text-slate-500" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-50/80 p-4 rounded-lg">
            <dt className="text-sm font-medium text-slate-500">Gender</dt>
            <dd className="mt-1 text-sm text-slate-900 font-semibold flex items-center">
              <span className={`mr-2 text-lg ${getGenderColorClass(bird.gender)}`}>
                {getGenderIcon(bird.gender)}
              </span>
              {bird.gender}
            </dd>
          </div>
          <div className="bg-slate-50/80 p-4 rounded-lg">
            <dt className="text-sm font-medium text-slate-500">Gender Determination</dt>
            <dd className="mt-1 text-sm text-slate-900 font-semibold">{bird.genderDeterminationMethod}</dd>
          </div>
          <div className="bg-slate-50/80 p-4 rounded-lg">
            <dt className="text-sm font-medium text-slate-500">Hatching Date</dt>
            <dd className="mt-1 text-sm text-slate-900 font-semibold">
              {new Date(bird.hatchingDate).toLocaleDateString()}
            </dd>
          </div>
          <div className="bg-slate-50/80 p-4 rounded-lg">
            <dt className="text-sm font-medium text-slate-500">Banding Date</dt>
            <dd className="mt-1 text-sm text-slate-900 font-semibold">
              {new Date(bird.bandingDate).toLocaleDateString()}
            </dd>
          </div>
        </div>
      </div>

      {/* Genetics Section */}
      {(bird.phenotype || bird.genotype) && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
            <BeakerIcon className="h-5 w-5 mr-2 text-slate-500" />
            Genetics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bird.phenotype && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <dt className="text-sm font-medium text-green-600">Phenotype</dt>
                <dd className="mt-1 text-sm text-green-900 font-semibold">{bird.phenotype}</dd>
              </div>
            )}
            {bird.genotype && (
              <div className="bg-cyan-50 p-4 rounded-lg border border-blue-200">
                <dt className="text-sm font-medium text-cyan-600">Genotype</dt>
                <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.genotype}</dd>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Provenance Section */}
      <div>
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <HomeIcon className="h-5 w-5 mr-2 text-slate-500" />
          Provenance & Transaction History
        </h3>
        
        {/* Acquisition Information */}
        <div className="bg-slate-50/80 p-6 rounded-lg mb-4">
          <h4 className="text-md font-semibold text-slate-900 mb-3 flex items-center">
            <span className="text-green-600 mr-2">📦</span>
            Acquisition Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-slate-500">Acquisition Type</dt>
              <dd className="mt-1 text-sm text-slate-900 font-semibold">{bird.provenance?.type || 'Unknown'}</dd>
            </div>
            {bird.provenance?.dateAcquired && (
              <div>
                <dt className="text-sm font-medium text-slate-500">Date Acquired</dt>
                <dd className="mt-1 text-sm text-slate-900 font-semibold">
                  {new Date(bird.provenance.dateAcquired).toLocaleDateString()}
                </dd>
              </div>
            )}
            {bird.provenance?.sellerName && (
              <>
                <div>
                  <dt className="text-sm font-medium text-slate-500">Seller Name</dt>
                  <dd className="mt-1 text-sm text-slate-900 font-semibold">{bird.provenance.sellerName}</dd>
                </div>
                {bird.provenance?.sellerContact && (
                  <div>
                    <dt className="text-sm font-medium text-slate-500">Seller Contact</dt>
                    <dd className="mt-1 text-sm text-slate-900 font-semibold">{bird.provenance.sellerContact}</dd>
                  </div>
                )}
              </>
            )}
            {bird.provenance?.price && (
              <div>
                <dt className="text-sm font-medium text-slate-500">Purchase Price</dt>
                <dd className="mt-1 text-sm text-slate-900 font-semibold">R{bird.provenance.price}</dd>
              </div>
            )}
            {bird.provenance?.invoiceNumber && (
              <div>
                <dt className="text-sm font-medium text-slate-500">Invoice Number</dt>
                <dd className="mt-1 text-sm text-slate-900 font-semibold">{bird.provenance.invoiceNumber}</dd>
              </div>
            )}
          </div>
          {bird.provenance?.notes && (
            <div className="mt-4">
              <dt className="text-sm font-medium text-slate-500">Purchase Notes</dt>
              <dd className="mt-1 text-sm text-slate-700">{bird.provenance.notes}</dd>
            </div>
          )}
        </div>

        {/* Sale Information */}
        {bird.provenance?.saleInfo && (
          <div className="bg-cyan-50 p-6 rounded-lg border border-blue-200">
            <h4 className="text-md font-semibold text-slate-900 mb-3 flex items-center">
              <span className="text-cyan-600 mr-2">💰</span>
              Sale Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bird.provenance.saleInfo.dateSold && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Date Sold</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">
                    {new Date(bird.provenance.saleInfo.dateSold).toLocaleDateString()}
                  </dd>
                </div>
              )}
              {bird.provenance.saleInfo.buyerName && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Buyer Name</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.buyerName}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.buyerContact && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Buyer Contact</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.buyerContact}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.buyerAddress && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Buyer Address</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.buyerAddress}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.salePrice && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Sale Price</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">R{bird.provenance.saleInfo.salePrice}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.saleInvoiceNumber && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Sale Invoice</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.saleInvoiceNumber}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.salePaymentMethod && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Payment Method</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.salePaymentMethod}</dd>
                </div>
              )}
              {bird.provenance.saleInfo.salePaymentReference && (
                <div>
                  <dt className="text-sm font-medium text-cyan-600">Payment Reference</dt>
                  <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.salePaymentReference}</dd>
                </div>
              )}
            </div>
            {bird.provenance.saleInfo.saleNotes && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-cyan-600">Sale Notes</dt>
                <dd className="mt-1 text-sm text-cyan-800">{bird.provenance.saleInfo.saleNotes}</dd>
              </div>
            )}
            {(bird.provenance.saleInfo.deliveryDate || bird.provenance.saleInfo.deliveryMethod || bird.provenance.saleInfo.trackingNumber) && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <h5 className="text-sm font-semibold text-blue-900 mb-2">Delivery Information</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {bird.provenance.saleInfo.deliveryDate && (
                    <div>
                      <dt className="text-sm font-medium text-cyan-600">Delivery Date</dt>
                      <dd className="mt-1 text-sm text-blue-900 font-semibold">
                        {new Date(bird.provenance.saleInfo.deliveryDate).toLocaleDateString()}
                      </dd>
                    </div>
                  )}
                  {bird.provenance.saleInfo.deliveryMethod && (
                    <div>
                      <dt className="text-sm font-medium text-cyan-600">Delivery Method</dt>
                      <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.deliveryMethod}</dd>
                    </div>
                  )}
                  {bird.provenance.saleInfo.trackingNumber && (
                    <div>
                      <dt className="text-sm font-medium text-cyan-600">Tracking Number</dt>
                      <dd className="mt-1 text-sm text-blue-900 font-semibold">{bird.provenance.saleInfo.trackingNumber}</dd>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Show message when bird is sold but no sale info available */}
        {bird.status === BirdStatus.SOLD && !bird.provenance?.saleInfo && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
                              <span className="font-semibold">⚠️ This bird is marked as sold</span> - view complete sale details in the Sales module.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BirdPhysicalInfo({ bird }: { bird: Bird }) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
        <ScaleIcon className="h-5 w-5 mr-2 text-slate-500" />
        Physical Information
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-cyan-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-cyan-600">Species Size</p>
              <p className="text-2xl font-bold text-blue-900">{bird.species?.size || 'N/A'}</p>
              <p className="text-sm text-cyan-600">cm</p>
            </div>
            <ScaleIcon className="h-8 w-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Species Weight</p>
              <p className="text-2xl font-bold text-green-900">{bird.species?.weight || 'N/A'}</p>
              <p className="text-sm text-green-600">grams</p>
            </div>
            <ScaleIcon className="h-8 w-8 text-green-400" />
          </div>
        </div>

        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Band Size</p>
              <p className="text-2xl font-bold text-purple-900">{bird.species?.bandSize || 'N/A'}</p>
              <p className="text-sm text-purple-600">mm</p>
            </div>
            <div className="h-8 w-8 rounded-full bg-purple-400 flex items-center justify-center">
              <span className="text-white text-xs font-bold">B</span>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Age</p>
              <p className="text-2xl font-bold text-orange-900">
                {Math.floor((new Date().getTime() - new Date(bird.hatchingDate).getTime()) / (1000 * 60 * 60 * 24 * 365.25))}
              </p>
              <p className="text-sm text-orange-600">years</p>
            </div>
            <CalendarIcon className="h-8 w-8 text-orange-400" />
          </div>
        </div>
      </div>

      {bird.species?.colorDescription && (
        <div className="bg-slate-50/80 p-6 rounded-lg">
          <h4 className="text-md font-semibold text-slate-900 mb-3">Species Color Description</h4>
          <p className="text-slate-700 leading-relaxed">{bird.species.colorDescription}</p>
        </div>
      )}
    </div>
  );
}

function BirdHealthRecords({ bird }: { bird: Bird }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <HeartIcon className="h-5 w-5 mr-2 text-slate-500" />
          Health Records
        </h3>
        <Link
          to={`/health-management?section=records&view=tests&birdId=${bird.id}`}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          <HeartIcon className="h-4 w-4 mr-2" />
          Manage health records
        </Link>
      </div>
      
      {bird.healthTests && bird.healthTests.length > 0 ? (
        <div className="space-y-4">
          {/* Health Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-cyan-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <BeakerIcon className="h-8 w-8 text-cyan-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-900">Total Tests</p>
                  <p className="text-2xl font-semibold text-cyan-600">{bird.healthTests.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CalendarIcon className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-900">Recent Tests</p>
                  <p className="text-2xl font-semibold text-green-600">
                    {bird.healthTests.filter(test => {
                      const testDate = new Date(test.testDate);
                      const sixMonthsAgo = new Date();
                      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                      return testDate >= sixMonthsAgo;
                    }).length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center">
                <HeartIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-900">Last Test</p>
                  <p className="text-sm font-semibold text-purple-600">
                    {bird.healthTests.length > 0 
                      ? new Date(Math.max(...bird.healthTests.map(t => new Date(t.testDate).getTime()))).toLocaleDateString()
                      : 'Never'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Health Tests List */}
          {bird.healthTests
            .sort((a, b) => new Date(b.testDate).getTime() - new Date(a.testDate).getTime())
            .map((test) => (
            <div key={test.id} className="border border-gray-200 rounded-lg p-6 bg-white hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-slate-900">{test.testName}</h4>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-slate-500">Tested by:</span>
                      <span className="ml-2 text-slate-900">{test.testeeName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-slate-500">Date:</span>
                      <span className="ml-2 text-slate-900">
                        {new Date(test.testDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-slate-500">Result:</span>
                    <span className="ml-2 text-slate-900 font-semibold">{test.result}</span>
                  </div>
                  {test.comment && (
                    <div className="mt-3">
                      <span className="font-medium text-slate-500">Comments:</span>
                      <p className="mt-1 text-slate-700">{test.comment}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {test.documents && test.documents.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h5 className="text-sm font-medium text-slate-900 mb-2">Attached Documents</h5>
                  <div className="flex flex-wrap gap-2">
                    {test.documents.map((doc) => (
                      <a
                        key={doc.id}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800 hover:bg-blue-200"
                      >
                        <DocumentTextIcon className="h-3 w-3 mr-1" />
                        {doc.type}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <HeartIcon className="mx-auto h-12 w-12 text-slate-400" />
          <h3 className="mt-2 text-sm font-semibold text-slate-900">No health records</h3>
          <p className="mt-1 text-sm text-slate-500">No health tests have been recorded for this bird yet.</p>
          <div className="mt-6">
            <Link
              to={`/health-management?section=records&view=tests&birdId=${bird.id}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
            >
              <HeartIcon className="h-4 w-4 mr-2" />
              Add first health test
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function BirdFamilyTree({ bird }: { bird: Bird }) {
  const [familyMembers, setFamilyMembers] = useState<{
    parents: { father?: Bird; mother?: Bird };
    siblings: Bird[];
    descendants: Bird[];
  }>({
    parents: {},
    siblings: [],
    descendants: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFamilyMembers = async () => {
      try {
        setLoading(true);
        
        // Test API accessibility first
        try {
          const testResponse = await api.get(`birds/${bird.id}/parents`);
        } catch (error) {
        }
        
        // Fetch all family members using the dedicated API endpoints
        const [parentsResponse, siblingsResponse, descendantsResponse] = await Promise.all([
          // Fetch parents
          api.get(`birds/${bird.id}/parents`).then(response => response.data).catch(() => ({ items: [] })),
          
          // Fetch siblings
          api.get(`birds/${bird.id}/siblings`).then(response => response.data).catch(() => ({ items: [] })),
          
          // Fetch descendants
          api.get(`birds/${bird.id}/descendants`).then(response => response.data).catch(() => ({ items: [] }))
        ]);
        
        // Process parents - separate father and mother based on gender
        const parents = (parentsResponse as any)?.items || [];
        const father = parents.find((p: any) => p.gender === 'MALE');
        const mother = parents.find((p: any) => p.gender === 'FEMALE');
        
        const familyData = {
          parents: { father, mother },
          siblings: (siblingsResponse as any)?.items || [],
          descendants: (descendantsResponse as any)?.items || []
        };
        
        setFamilyMembers(familyData);
      } catch {
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyMembers();
  }, [bird.id]);

  // Helper function to render a family member with proper styling
  const renderFamilyMember = (bird: Bird, type: 'parent' | 'sibling' | 'descendant') => {
    const displayInfo = getBirdDisplayInfo(bird);
    
    const baseClasses = "text-sm rounded px-2 py-1";
    const typeClasses = {
      parent: "text-cyan-800 bg-blue-100",
      sibling: "text-green-800 bg-green-100", 
      descendant: "text-purple-800 bg-purple-100"
    };
    
    return (
      <div 
        key={bird.id}
        className={`${baseClasses} ${typeClasses[type]} ${displayInfo.isUnbanded ? 'border-2 border-dashed border-orange-300' : ''}`}
        title={displayInfo.tooltip}
      >
        <div className="flex items-center gap-1">
          {displayInfo.isUnbanded && (
            <span className="text-orange-600 text-xs">🐤</span>
          )}
          <span className="font-medium">{displayInfo.displayId}</span>
          <span className="text-xs opacity-75">({displayInfo.speciesName})</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
          <UserGroupIcon className="h-5 w-5 mr-2 text-slate-500" />
          Family Tree
        </h3>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
        <UserGroupIcon className="h-5 w-5 mr-2 text-slate-500" />
        Family Tree
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Parents */}
        <div className="bg-cyan-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-blue-900 mb-4">Parents</h4>
          <div className="space-y-3">
            {familyMembers.parents.father && (
              <div className="flex items-center space-x-2">
                <span className="text-cyan-600 font-bold">{GENDER_ICON_MALE}</span>
                {renderFamilyMember(familyMembers.parents.father, 'parent')}
              </div>
            )}
            {familyMembers.parents.mother && (
              <div className="flex items-center space-x-2">
                <span className="text-pink-600 font-bold">{GENDER_ICON_FEMALE}</span>
                {renderFamilyMember(familyMembers.parents.mother, 'parent')}
              </div>
            )}
            {!familyMembers.parents.father && !familyMembers.parents.mother && (
              <p className="text-sm text-slate-500">No parent information available</p>
            )}
          </div>
        </div>

        {/* Siblings */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-green-900 mb-4">Siblings</h4>
          {familyMembers.siblings.length > 0 ? (
            <div className="space-y-2">
              {familyMembers.siblings.map((sibling) => renderFamilyMember(sibling, 'sibling'))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No siblings recorded</p>
          )}
        </div>

        {/* Offspring */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 lg:col-span-2">
          <h4 className="text-md font-semibold text-purple-900 mb-4">Offspring</h4>
          {familyMembers.descendants.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {familyMembers.descendants.map((descendant) => renderFamilyMember(descendant, 'descendant'))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No offspring recorded</p>
          )}
        </div>
      </div>
    </div>
  );
}

function BirdNotesAndDocuments({ bird }: { bird: Bird }) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
        <DocumentTextIcon className="h-5 w-5 mr-2 text-slate-500" />
        Notes & Documents
      </h3>
      
      {/* Notes Section */}
      <div>
        <h4 className="text-md font-semibold text-slate-900 mb-4">Notes</h4>
        {bird.notes && bird.notes.length > 0 ? (
          <div className="space-y-4">
            {bird.notes.map((note) => (
              <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex items-start justify-between">
                  <h5 className="font-semibold text-slate-900">{note.heading}</h5>
                  <span className="text-sm text-slate-500">
                    {new Date(note.date).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-2 text-slate-700">{note.detail}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50/80 rounded-lg">
            <DocumentTextIcon className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">No notes recorded</p>
          </div>
        )}
      </div>

      {/* Documents Section */}
      <div>
        <h4 className="text-md font-semibold text-slate-900 mb-4">Documents</h4>
        {bird.documents && bird.documents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bird.documents.map((doc) => (
              <div key={doc.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="h-8 w-8 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{doc.type}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(doc.dateAdded).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-cyan-700 bg-blue-100 hover:bg-blue-200"
                >
                  View Document
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-slate-50/80 rounded-lg">
            <DocumentTextIcon className="mx-auto h-8 w-8 text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">No documents attached</p>
          </div>
        )}
      </div>
    </div>
  );
} 